import React from "react";
import { Standing } from "../../../models/Standing";
import { StandingsPrediction } from "../../../models/StandingsPrediction";
import { PredictionScore, scorePrediction } from "../../../utils/predictionScore";

/** Column identity colours: the hosts in config order, then the guest, then the compared team's hosts. */
export interface ColumnColor {
  fg: string;
  soft: string;
}

export const HOST_COLORS: ColumnColor[] = [
  { fg: "#ff9a5c", soft: "rgba(255, 154, 92, 0.16)" },
  { fg: "#6cb6ff", soft: "rgba(108, 182, 255, 0.16)" },
];
export const GUEST_COLOR: ColumnColor = { fg: "#c9a4ff", soft: "rgba(201, 164, 255, 0.16)" };
export const RIVAL_COLORS: ColumnColor[] = [
  { fg: "#5ee08a", soft: "rgba(94, 224, 138, 0.16)" },
  { fg: "#ff7ab6", soft: "rgba(255, 122, 182, 0.16)" },
];

export const colorVars = (color: ColumnColor): React.CSSProperties =>
  ({ "--c": color.fg, "--soft": color.soft } as React.CSSProperties);

/** One predictor shown side by side: a host, the division's guest, or a host of the compared team. */
export interface Column {
  key: string;
  steamId: string;
  name: string;
  kind: "host" | "guest" | "rival";
  /** The compared team's name, for a rival. */
  team?: string;
  color: ColumnColor;
  /** Predicted rank by team id for the shown division; null when there is no prediction. */
  ranks: Map<string, number> | null;
  /** Still loading this predictor's predictions. */
  loading: boolean;
  /** The predictions request failed. */
  failed: boolean;
  /** Null until the division has games played. */
  score: PredictionScore | null;
  /** Predicted teams that are in the division's standings. */
  matched: number;
}

export const makeColumn = (
  base: Pick<Column, "key" | "steamId" | "name" | "kind" | "team" | "color" | "loading" | "failed">,
  prediction: StandingsPrediction | null | undefined,
  standings: Standing[]
): Column => {
  const teams = prediction?.teams ?? [];
  if (teams.length === 0) return { ...base, ranks: null, score: null, matched: 0 };
  const ranks = new Map(teams.map((t) => [t.teamId, t.predictedRank]));
  const inStandings = new Set(standings.map((s) => s.team.teamId));
  return {
    ...base,
    ranks,
    score: scorePrediction(teams, standings),
    matched: teams.filter((t) => inStandings.has(t.teamId)).length,
  };
};

export const initialOf = (name: string): string => (name.trim()[0] ?? "?").toUpperCase();

export const formatAvg = (value: number | null | undefined): string =>
  value === null || value === undefined ? "–" : value.toFixed(2);

/** One division's scores, one per host in host order (null when that host has no scored prediction). */
export interface DivisionResult {
  division: string;
  scores: (PredictionScore | null)[];
}

/** Who won a division between two hosts, on the two decimals the page shows: 0 first, 1 second, -1 tie. */
export const divisionWinner = (scores: (PredictionScore | null)[]): 0 | 1 | -1 | null => {
  const [a, b] = scores;
  if (!a || !b) return null;
  const c = Math.round(a.score * 100) - Math.round(b.score * 100);
  return c < 0 ? 0 : c > 0 ? 1 : -1;
};
